import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Collapse } from '@mui/material';
import { LockKeyhole, Plus, Trash2 } from 'lucide-react';

import { toastPromise } from '../../common/utils';
import { useGetProjectById } from '../../hooks';
import { useDeleteUserDefinedExpense } from '../../hooks/tanstack/useExpenseHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import routes from '../../routes';
import cn from '../../utils/cn';
import WarningDialog from '../WarningDialog';

type ExpenseItem = {
  label: string;
  value: number;
  isLocked?: boolean;
  type?: 'default' | 'custom';
};

type ExpenseCollapseProps = {
  title: string;
  group: string;
  items: ExpenseItem[];
  isSelectable?: boolean;
  isDeletable?: boolean;
  handleOpenCreateExpenseDialog?: (group: string) => void;
};

const ExpenseCollapse = ({
  title,
  items,
  isSelectable = true,
  isDeletable,
  group,
  handleOpenCreateExpenseDialog,
}: ExpenseCollapseProps) => {
  const [open, setOpen] = useState(true);

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState<{
    open: boolean;
    item: ExpenseItem | null;
  }>({
    open: false,
    item: null,
  });

  const { orgId, projectId } = useGetRouteParams();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { mutateAsync: deleteExpense, isPending: isDeletingExpense } =
    useDeleteUserDefinedExpense({
      onSuccess: () => {
        handleCloseConfirmDeleteDialog();
      },
    });

  const navigate = useNavigate();

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleOpenConfirmDeleteDialog = (item: ExpenseItem) => {
    setOpenConfirmDeleteDialog({
      open: true,
      item,
    });
  };

  const handleCloseConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog({
      open: false,
      item: null,
    });
  };

  const handleSelect = (id: number) => {
    navigate(
      routes.projects.expenses.detail
        .replace(':expense_id', id.toString())
        .replace(':project_id', projectId.toString())
        .replace(':org_id', orgId.toString())
    );
  };

  const handleDeleteExpense = () => {
    const item = openConfirmDeleteDialog.item;

    if (!item) {
      return;
    }

    const res = deleteExpense({
      orgId,
      projectId,
      expenseId: item.value,
    });

    toastPromise({
      promise: res,
      content: `Delete expense ${item.label} successfully`,
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-1"
      >
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
        <label className="font-bold capitalize">{title}</label>
      </button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className="border-gray-300 mb-4 ml-[11px] mt-2 flex flex-col overflow-y-hidden border-l pl-4">
          {items.map((item: ExpenseItem) =>
            item.isLocked && !project?.is_guest_project ? null : (
              <div key={item.value} className="flex justify-between">
                <div
                  className={cn(
                    'flex transition justify-between w-full items-center gap-1 rounded-[4px]'
                  )}
                >
                  <div
                    role={
                      isSelectable && item.type === 'custom' ? 'button' : 'none'
                    }
                    className={cn(
                      'w-full p-2 text-start font-normal flex gap-2 items-center',
                      {
                        'cursor-pointer': item.type === 'custom',
                      }
                    )}
                    onClick={() =>
                      item.type === 'custom' &&
                      isSelectable &&
                      handleSelect(item.value)
                    }
                  >
                    <p>{item.label}</p>
                    {item.type === 'custom' && (
                      <span className="rounded-md font-bold bg-[#F5F5F5] text-[#666666] px-[10px] py-1 text-sm">
                        Custom
                      </span>
                    )}
                  </div>
                  {item.isLocked === true ? (
                    <LockKeyhole size={20} color="#666666" />
                  ) : (
                    isDeletable &&
                    item.type === 'custom' && (
                      <button
                        type="button"
                        onClick={() => handleOpenConfirmDeleteDialog(item)}
                      >
                        <Trash2 size={20} color="#666666" />
                      </button>
                    )
                  )}
                </div>
              </div>
            )
          )}
          {handleOpenCreateExpenseDialog && (
            <button
              onClick={() => handleOpenCreateExpenseDialog?.(group)}
              type="button"
              className="flex px-1 text-primary-red items-center"
            >
              <Plus size={20} />
              <p>Add</p>
            </button>
          )}
        </div>
        {openConfirmDeleteDialog.item !== undefined ? (
          <WarningDialog
            open={openConfirmDeleteDialog.open}
            isPending={isDeletingExpense}
            primaryActionText="Yes, delete it"
            secondaryActionText="Cancel"
            handleSecondaryAction={handleCloseConfirmDeleteDialog}
            handlePrimaryAction={handleDeleteExpense}
            content={
              'If you delete this custom expense, it will be deleted from all models using it'
            }
          />
        ) : null}
      </Collapse>
    </div>
  );
};

export default ExpenseCollapse;
